import {BehaviorSubject, Observable, of, throwError as observableThrowError} from 'rxjs';
import {catchError, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import { AppConfig } from '../../app.config';
import { GlobalSpinnerService } from '../../core/spinner/global-spinner.service';
import { ErrorHandlerService } from '../../core/settings/error-handler.service';
import * as xml2js from 'xml2js';


export interface DPAMRequestOverridableRedemptionDetailsI {
  redemptionInstructionsCustomer: string;
  redemptionInstructionsStaff: string;
  digitalTerms: string;
  compatibility: string;
  legalDisclaimer: string;
}

export interface DPAMRequestRedemptionDetailsI {
  overridableRedemptionDetails: DPAMRequestOverridableRedemptionDetailsI;
  expiration: string;
  serviceLevel: string;
  productNotes: string;
}

export interface DPAMRequestMarketingDetailsI {
  externalProductName: string;
  externalProductNameShort: string;
  marketingDescriptionShort: string;
  marketingDescriptionLong: string;
  searchTerms: string;
}

export interface DPAMRequestProductRequestI {
  upc: string;
  productName: string;
  marketingDetails: DPAMRequestMarketingDetailsI;
  redemptionDetails: DPAMRequestRedemptionDetailsI;
  mdmProductId: number;
}

export interface DPAMRequestDeliveryChannelsI {
  overridableRedemptionDetails: DPAMRequestOverridableRedemptionDetailsI;
  channelType: string;
  packUpcNumber: string;
  packUpcId: number;
}

export interface DPAMRequestI {
  currentAssignee: string;
  currentAssigneeFullName: string;
  currentStatus: string;
  deliveryChannels: DPAMRequestDeliveryChannelsI[];
  languageVersion: LanguageVersionsI;
  firstLanguageVersion: LanguageVersionsI;
  processInstanceId: number;
  productRequest: DPAMRequestProductRequestI;
  requestPrepopulated: boolean;
  productDigital: boolean;
  latestVersionInMdm: boolean;
}

export interface LanguageVersionsI {
  id: number;
  currentVersionInMdm: number;
  language: {
    isoCode: string;
    description: string;
  };
  requestVersion: number;
  versionCreated: string;
}

export interface DPAMRequestRes {
  comments: string;
  data: DPAMRequestData[];
  email: string;
}

export interface DPAMGroupRes {
  email: string;
  name: string;
  attributes: GroupAttributeI[];
  baseUPC: string;
  comments: string;
  data: {dataPath: string, request: DPAMRequestI}[];
}

export interface DPAMRequestData {
  dpamRequest: DPAMRequestI;
  dpamRequestFinalFiles: DPAMFile[];
  dpamRequestOriginalFiles: DPAMFile[];
}

export interface DPAMFile {
  fileContent: string;
  fileName: string;
  fileSizeInKB: number;
  mimeType: string;
  type: string;
}
export interface ProductStateI {
  productData: DPAMRequestRes | null;
  selectedProductData: DPAMRequestRes | null;
  groupData: DPAMGroupRes | null;
}
const defaultConfig: ProductStateI = {
  selectedProductData: null,
  productData: null,
  groupData: null
};
export interface GroupAttributeI {
  displayName: 'string';
  fieldName: 'string';
  mdmIdentifier: 'string';
  selectedAttribute: 'string';
}

 export enum ViewLevel {
  Group = 'Group',
  Product = 'Product'
}

@Injectable({providedIn: 'root'})
export class ProductSetupService {
  productState: ProductStateI = Object.assign({}, defaultConfig);
  searchStateSource: BehaviorSubject<ProductStateI> = new BehaviorSubject(this.productState);
  productState$: Observable<ProductStateI> = this.searchStateSource.asObservable();
  loading$ = new BehaviorSubject(null);

  constructor(private http: HttpClient,
    private appConfig: AppConfig,
    private globalSpinner: GlobalSpinnerService,
    private errorService: ErrorHandlerService) {
    this.setInitState();
  }

  getProduct(upcId: string, query: string): Observable<DPAMRequestRes> {
    this.globalSpinner.startLoading();

    function createDpamRequestArray(res: DPAMRequestRes):  DPAMRequestData[] {
      // if english request exists move it to index 0
      if (res.data.find((r: DPAMRequestData) => r.dpamRequest.languageVersion.language.isoCode === 'EN') !== undefined) {
        res.data = [
          res.data.find((r: DPAMRequestData) => r.dpamRequest.languageVersion.language.isoCode === 'EN'),
          ...res.data.filter((r: DPAMRequestData) => r.dpamRequest.languageVersion.language.isoCode !== 'EN')
        ];
      }
      return res.data;
    }

    return this.http.get(`${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${upcId}/upc-data.json?${query}`)
      .pipe(
        map((res: DPAMRequestRes) => {
          res.data = createDpamRequestArray(res);
          return res;
        }),
        tap(data => this.setProduct(data)),
        map((res: DPAMRequestRes) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
    );
  }

  getProductByPath(path: string): Observable<DPAMRequestRes> {
    return this.http.get<any>(path);
  }

  updateProduct(upcId: string, query: string, body): Observable<any> {
    this.globalSpinner.startLoading();
    const headers = new HttpHeaders({'x-ms-blob-type': 'BlockBlob'});
    return this.http.put(`${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${upcId}/upc-data.json?${query}`, body, {headers})
      .pipe(
        map((res: DPAMRequestRes) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  submitRequest(upcId: string, query: string): Observable<any> {
    this.globalSpinner.startLoading();
    const body = '<?xml version="1.0" encoding="utf-8"?><Tags><TagSet><Tag><Key>status</Key><Value>submitted</Value></Tag></TagSet></Tags>';
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/xml');

    return this.http.put(`${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${upcId}/upc-data.json?${query}&comp=tags`, body, {headers})
      .pipe(
        map((res: DPAMRequestRes) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  getTags(upcId: string, query: string): Observable<any> {
    this.globalSpinner.startLoading();
    return this.http.get(`${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${upcId}/upc-data.json?${query}&comp=tags`, {responseType: 'text'})
      .pipe(
        switchMap(async xml => this.parseXmlToJson(xml)),
        mergeMap(({TAGS}) => {
          if (!TAGS) return of([]);
          return of(TAGS?.TAGSET[0].TAG);
        }),
        map((res: any) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  getGroupTags(groupId: string, query: string): Observable<any> {
    this.globalSpinner.startLoading();
    return this.http.get(`${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${groupId}/group-data.json?${query}&comp=tags`, {responseType: 'text'})
      .pipe(
        switchMap(async xml => this.parseXmlToJson(xml)),
        mergeMap(({TAGS}) => {
          if (!TAGS) return of([]);
          return of(TAGS?.TAGSET[0].TAG);
        }),
        map((res: any) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  updateProductByPath(path: string, body): Observable<any> {
    this.globalSpinner.startLoading();
    const headers = new HttpHeaders({'x-ms-blob-type': 'BlockBlob'});
    return this.http.put(`${path}`, body, {headers})
      .pipe(
        map((res: DPAMRequestRes) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  getFirstProduct(path: string): Observable<any> {
    return this.getProductByLink(path)
      .pipe(
        tap(data => this.setFirstProduct(data))
      );
  }

  getSelectedProduct(path: string): Observable<any> {
    return this.getProductByLink(path)
      .pipe(
        tap(data => this.setSelectedProduct(data))
      );
  }

  getGroup(groupId: string, query: string): Observable<DPAMGroupRes> {
    this.globalSpinner.startLoading();
    return this.http.get(`${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${groupId}/group-data.json?${query}`)
      .pipe(
        tap((data: DPAMGroupRes) => this.setGroup(data)),
        tap((data: DPAMGroupRes) => this.getFirstProduct(data.data[0].dataPath).subscribe()),
        map((res: DPAMGroupRes) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  submitGroupRequest(groupId: string, query: string): Observable<any> {
    this.globalSpinner.startLoading();
    const body = '<?xml version="1.0" encoding="utf-8"?><Tags><TagSet><Tag><Key>status</Key><Value>submitted</Value></Tag></TagSet></Tags>';
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/xml');

    return this.http.put(`${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${groupId}/group-data.json?${query}&comp=tags`, body, {headers})
      .pipe(
        map((res: DPAMRequestRes) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  setInitState() {
    this.searchStateSource.next(Object.assign(this.productState, defaultConfig));
  }

  setProduct(productData: DPAMRequestRes) {
    this.searchStateSource.next(Object.assign(this.productState, {productData}));
  }

  setFirstProduct(firstProductData: DPAMRequestRes) {
    this.searchStateSource.next(Object.assign(this.productState, {selectedProductData: firstProductData}));
  }

  setSelectedProduct(selectedProductData: DPAMRequestRes) {
    this.searchStateSource.next(Object.assign(this.productState, {selectedProductData}));
  }

  setGroup(groupData: DPAMGroupRes) {
    this.searchStateSource.next(Object.assign(this.productState, {groupData}));
  }

  private getProductByLink(path: string): Observable<DPAMRequestRes> {
    this.globalSpinner.startLoading();
    return this.http.get(path)
      .pipe(
        map((res: DPAMRequestRes) => this.handleResponse(res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  private handleResponse(res: any): any {
    this.globalSpinner.finishLoading();
    return res || {};
  }

  private handleError(error: Response | any) {
    this.globalSpinner.finishLoading();
    this.errorService.showError(error);
    return observableThrowError(error);
  }

  private async parseXmlToJson(xml): Promise<any> {
    const parser = new xml2js.Parser({strict: false, trim: true});
    return await parser.parseStringPromise(xml);
  }
}
